import { TrackType } from "../generated/gql/graphql";
import { STEM_MODEL_ID } from "../statics/constants";

/**
 * Parsed stem track identifier derived from a synthetic track id
 * in the `{trackId}@{modelId}` format.
 */
export type StemIds = {
  /** Full synthetic stem track id before parsing. */
  id: string;
  /** Source track id. */
  trackId: string;
  /** Stem model id. */
  modelId: STEM_MODEL_ID;
};

/**
 * Parses a synthetic stem track id in `{trackId}@{modelId}` format.
 * Returns `null` when the id is not a stem id or the model id is unrecognised.
 */
export const parseStemTrackId = (id: string): StemIds | null => {
  if (!id.includes("@")) return null;

  const parts = id.split("@");
  if (parts.length !== 2) return null;

  const [trackId, modelIdRaw] = parts;
  if (!trackId || !modelIdRaw) return null;

  const modelId = Object.values(STEM_MODEL_ID).find(
    (value) => value === modelIdRaw,
  );
  if (!modelId) return null;

  return { id, trackId, modelId };
};

/** Derives a Track's type from its id. */
export const getTrackType = (id: string): TrackType | null =>
  parseStemTrackId(id) ? TrackType.Stem : null;

/** A mixed list of track ids partitioned into stems, plain tracks, and base ids. */
export type SplitTrackIds = {
  stemTrackIds: StemIds[];
  plainTrackIds: string[];
  baseTrackIds: string[];
};

/**
 * Partitions a mixed list of track ids into stem ids, plain track ids, and the
 * deduplicated set of base track ids needed to fetch underlying track data for
 * both groups.
 */
export const splitTrackIds = (trackIds: readonly string[]): SplitTrackIds => {
  const stemTrackIds = trackIds
    .map((id) => parseStemTrackId(id))
    .filter((item): item is StemIds => item !== null);

  const plainTrackIds = trackIds.filter((id) => !id.includes("@"));

  // Stem variants reuse the same base track data; deduplicate across both sets.
  const baseTrackIds = Array.from(
    new Set([...plainTrackIds, ...stemTrackIds.map(({ trackId }) => trackId)]),
  );

  return { stemTrackIds, plainTrackIds, baseTrackIds };
};
